import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../services/api'
import { useToast } from '../context/ToastContext'

// Singleton reminder poller.
//
// Before this hook, GlobalReminders + Calendar each ran their own 60s
// setInterval against GET /api/calendar/reminders — every extra page that
// wanted to react to reminder state added another poll.  This hook consolidates
// every subscriber onto a SINGLE module-scoped interval + shared state, and
// only starts polling when the first component mounts + stops when the last
// unmounts.
//
// Contract:
//   const { reminders, refresh, dismiss, dismissAll } = useReminders()
//   dismiss(row)                          → PUT /:id/dismiss { due_at }
//   dismiss(row, { permanent: true })     → PUT /:id/dismiss?permanent=true { due_at }
//   dismiss(row, { minutes: 15 })         → PUT /:id/snooze { minutes: 15, due_at }
//   dismiss(row, { snooze_until: 'ISO' }) → PUT /:id/snooze { snooze_until, due_at }
//   dismissAll([rowA, rowB], opts)        → the same PUT once PER ROW, each
//                                           echoing its own due_at
// `row` is a reminder exactly as GET /reminders returned it.  The older
// shapes still work: dismiss(id, opts) looks the row up in the current poll
// to find its due time, and dismiss(id, { due_at, ...opts }) passes it
// explicitly.  dismiss() resolves to an outcome — { status: 'ok' | 'changed'
// | 'gone' | 'failed' } — which callers are free to ignore; dismissAll()
// resolves to one outcome per row, in the order given.
//
// dismissAll is for GlobalReminders' one-card-per-event view (2026-10-12): an
// imported Amicus event often carries five reminders, and the card that
// groups them dismisses or snoozes exactly the rows it showed — never the
// event's other reminders, never one that has not come due.  Each row gets
// the single-row treatment below (optimistic hide, due_at echo, 409 →
// dropped and never remembered, 404 → re-poll) with three differences: the
// 409/404 re-poll runs ONCE after every PUT has settled rather than once per
// row; the hook toasts each distinct 409 message once rather than once per
// row; and a row whose PUT failed outright (5xx, network, 403) goes BACK on
// its card with an error toast, instead of staying hidden for the session as
// a single ✕'s does — the server recorded nothing, so the card stays for it.
//
// Dismiss and snooze are PERSONAL (2026-09-25): the server records them for
// the signed-in person only, so everyone else who gets the same reminder
// still sees it, and they now succeed for anyone who can see the event (an
// everyone-reminder used to 403 unless you owned the event or were an admin,
// and this hook then hid it for the session only — it came back on reload).
// The local hide below is kept as a fallback for a rolling deploy, when an
// older API may still answer 403: the card then stays hidden for this
// session, exactly as before.
//
// A dismissed reminder comes back when its event MOVES (code review of
// 9ff73f1, finding #1; contract C1).  The server re-arms a reminder whose due
// time changes — drag, bulk reschedule, a discovery extension — so a
// continued hearing still gets its "1 day before".  Two things here follow
// from that:
//   • Everything this hook remembers locally is keyed by OCCURRENCE — the
//     reminder id plus the due time it had when the user acted — not by id
//     alone.  A move keeps the same reminder id, so an id-keyed hide would
//     keep the re-armed reminder hidden until the page was reloaded, which is
//     the very bug the server fix exists to cure.
//   • Each dismiss/snooze echoes back the reminder_due_at the user was looking
//     at.  If the event moved in the meantime the server answers 409
//     reminder_changed and records nothing, so a click aimed at the OLD time
//     can never silence the NEW one.  The card is dropped, the user is told
//     why, and a fresh poll runs at once — so if the new time is already
//     due, the card is back straight away at that time.
//   • Once the server has CONFIRMED a dismiss or snooze, the server decides.
//     The local hide exists only for the window before that (a poll already
//     on the wire when the user clicked) and for a PUT that failed (an older
//     API's 403, a network blip).  After confirmation, the first poll SENT
//     afterwards drops it: the server is now filtering the reminder for this
//     person, so if it lists that occurrence again the database re-armed it.
//     Keeping the hide would swallow exactly that case — a hearing dragged
//     away and then back (the drag toast's Undo), or dragged to a time that
//     is not due yet and back, returns the SAME occurrence, re-armed, and an
//     occurrence-keyed hide would keep it hidden until reload.  (Code review
//     of 9ff73f1, verify pass on finding #1.)

const POLL_MS = 60000
// Bound the dismissed set — otherwise a session with hundreds of reminders
// dismissed over hours slowly accumulates unbounded memory.  FIFO eviction:
// when we hit the cap the oldest entry is dropped, which just means a
// very-stale reminder MIGHT resurface if the server still serves it —
// acceptable trade-off vs unbounded growth.
const DISMISSED_CAP = 500

const REMINDER_CHANGED_FALLBACK =
  'This reminder changed — its event was moved or edited. It will remind you again at the new time.'

// Module-scoped singleton state — shared across every subscriber.
let reminders = []
// Occurrence keys (see occurrenceKey) — session-scoped so a stale poll can't
// re-surface a reminder the user already dismissed.
const dismissedIds = new Set()
// occurrence key → epoch ms.  A snoozed (or repeat-dismissed) reminder stays
// hidden locally until then, so a poll racing the PUT — or an older API that
// refused it — cannot pop it straight back up.  Expired entries are pruned.
const snoozedUntil = new Map()
const subscribers = new Set()   // Set<setState-callback>
let intervalId = null
let inFlight = null             // dedupes concurrent fetches
// Bumped whenever a poll that is already on the wire can no longer be
// trusted: after a 409 (it may still carry the reminder at its OLD time, and
// the old occurrence is no longer hidden), after a 404 (it may predate the
// save that re-created the reminder under a new id), and on logout (it
// belongs to the previous user).  A poll drops a response whose epoch is out
// of date; the 409/404 paths send a fresh one at once (startPoll), otherwise
// the next tick replaces it.
let pollEpoch = 0
// Numbers every poll as it is SENT.  confirmedAt maps an occurrence key to
// the pollSeq of the latest poll sent before the server confirmed the user's
// dismiss/snooze of it.  A poll numbered higher was sent after the server
// recorded it, so its answer is authoritative and the key's local hide is
// dropped (see the header).  Entries leave the map on that poll, so it holds
// at most the dismissals of the last minute or so.
let pollSeq = 0
const confirmedAt = new Map()

/**
 * The key for one OCCURRENCE of a reminder: its id plus the time it is due.
 * The due time is normalised to epoch ms so an ISO string and a Date for the
 * same instant agree.  A row without a due time (an API that predates
 * reminder_due_at) keys on the id alone, exactly as before.
 */
export function occurrenceKey(id, dueAt) {
  let due = ''
  if (dueAt != null && dueAt !== '') {
    const ms = new Date(dueAt).getTime()
    due = Number.isFinite(ms) ? String(ms) : String(dueAt)
  }
  return `${id}@${due}`
}

function rememberDismissed(key) {
  if (dismissedIds.has(key)) return
  dismissedIds.add(key)
  if (dismissedIds.size > DISMISSED_CAP) {
    // Set preserves insertion order — drop oldest.
    const oldest = dismissedIds.values().next().value
    dismissedIds.delete(oldest)
    confirmedAt.delete(oldest)
  }
}

/**
 * Clear session-scoped dismiss + snooze state.  Called from AuthContext on
 * logout so a user signing out doesn't leave another user (on the same
 * device — kiosk / shared laptop) with pre-dismissed reminders they never
 * saw.  Also clears in-memory reminders + snapshot so the next login
 * starts from a clean slate, and discards any poll still in flight (it was
 * made with the previous user's session).
 */
export function resetReminderState() {
  dismissedIds.clear()
  snoozedUntil.clear()
  confirmedAt.clear()
  pollEpoch += 1
  // Forget the discarded poll's slot too, so the next user's first poll is
  // sent rather than handed the previous user's (whose answer pollEpoch now
  // throws away — the reminders would stay empty until the next tick).  Safe:
  // that poll's own cleanup only clears the slot if it still holds it.
  inFlight = null
  reminders = []
  emit()
}

function emit() {
  for (const setState of subscribers) {
    setState(reminders)
  }
}

async function fetchNow() {
  if (inFlight) return inFlight
  return startPoll()
}

// Sends a poll even when one is already on the wire.  fetchNow() shares the
// in-flight poll, which is right for the 60-second tick but wrong after a
// dismiss answered 409 or 404: that poll may have been SENT before the event
// was moved or re-saved, so it cannot show the reminder at its new time or
// under its new id.  The caller bumps pollEpoch first, so the older poll's
// answer is discarded whenever it lands, and cannot overwrite this one.
// (Seam audit of the 9ff73f1 follow-up fixes.)
function startPoll() {
  const epoch = pollEpoch
  const seq = ++pollSeq
  const run = (async () => {
    try {
      const res = await api.get('/api/calendar/reminders')
      // Superseded while on the wire — see pollEpoch.
      if (epoch !== pollEpoch) return
      // Sent after the server confirmed these — its answer now decides.
      for (const [key, sentBefore] of confirmedAt) {
        if (seq > sentBefore) {
          dismissedIds.delete(key)
          snoozedUntil.delete(key)
          confirmedAt.delete(key)
        }
      }
      const now = Date.now()
      const rows = (res.data || []).filter(r => {
        const key = occurrenceKey(r.reminder_id, r.reminder_due_at)
        return !dismissedIds.has(key) && !((snoozedUntil.get(key) || 0) > now)
      })
      reminders = rows
      emit()
    } catch {
      // Best-effort — silently swallow so a transient network blip
      // doesn't spam toasts or crash listeners.
    }
  })()
  // Cleared only while it is still the CURRENT poll: an older poll landing
  // after startPoll() replaced it must not wipe the newer one's slot.  The
  // .finally callback runs a microtask later, after `tracked` is assigned.
  const tracked = run.finally(() => { if (inFlight === tracked) inFlight = null })
  inFlight = tracked
  return tracked
}

function startPolling() {
  if (intervalId) return
  fetchNow()
  intervalId = setInterval(fetchNow, POLL_MS)
}

function stopPolling() {
  if (!intervalId) return
  clearInterval(intervalId)
  intervalId = null
}

function rememberSnoozed(key, untilMs) {
  if (!Number.isFinite(untilMs)) return
  const now = Date.now()
  for (const [k, v] of snoozedUntil) if (v <= now) snoozedUntil.delete(k)
  snoozedUntil.set(key, untilMs)
}

// Accepts a reminder row, or an id (with an optional opts.due_at).  For a bare
// id the due time is looked up in the current poll so the old call shape
// still echoes it.  Never falls back to `row.id` — on a GET /reminders row
// that is the EVENT id (the query selects e.*), not the reminder's.
function resolveTarget(target, opts) {
  if (target && typeof target === 'object') {
    const due = target.reminder_due_at !== undefined ? target.reminder_due_at : opts.due_at
    return { id: target.reminder_id, due }
  }
  let due = opts.due_at
  if (due === undefined) {
    const row = reminders.find(r => String(r.reminder_id) === String(target))
    if (row) due = row.reminder_due_at
  }
  return { id: target, due }
}

// `deferPoll` is internal (dismissReminders below): the 409/404 branches still
// discard any poll already on the wire at once — that is what stops a stale
// answer putting the OLD time back — but leave SENDING the fresh poll to the
// caller, which sends one after the whole batch has settled.
async function dismissReminder(target, opts = {}, { deferPoll = false } = {}) {
  const { id, due } = resolveTarget(target, opts)
  const key = occurrenceKey(id, due)
  const isSnooze = opts.minutes != null || opts.snooze_until
  // Contract C1: echo the due time back so the server can refuse a click
  // aimed at a time the reminder no longer has.  Omitted when unknown — the
  // server then skips the check, as it does for the desktop app.
  const echo = due != null && due !== '' ? { due_at: due } : {}
  // Optimistic: hide immediately from the current view.
  if (isSnooze) {
    rememberSnoozed(key, opts.snooze_until
      ? new Date(opts.snooze_until).getTime()
      : Date.now() + Number(opts.minutes) * 60000)
  } else {
    rememberDismissed(key)
  }
  reminders = reminders.filter(r => String(r.reminder_id) !== String(id))
  emit()

  try {
    if (isSnooze) {
      const payload = opts.snooze_until
        ? { snooze_until: opts.snooze_until }
        : { minutes: opts.minutes }
      await api.put(`/api/calendar/reminders/${id}/snooze`, { ...payload, ...echo })
      confirmedAt.set(key, pollSeq)
      return { status: 'ok' }
    }
    const qs = opts.permanent ? '?permanent=true' : ''
    const res = await api.put(`/api/calendar/reminders/${id}/dismiss${qs}`, echo)
    // A repeating reminder is not gone — the server snoozed it for this
    // person and it re-fires after repeat_minutes.  Swap the session-long
    // hide for a timed one so the next fire actually shows.  (Belt and
    // braces since confirmedAt: the first poll sent after this confirmation
    // drops the local hide altogether and leaves the timing to the server.)
    if (res?.data?.mode === 'recurring') {
      dismissedIds.delete(key)
      rememberSnoozed(key, new Date(res.data.next_fire).getTime())
    }
    confirmedAt.set(key, pollSeq)
    return { status: 'ok', mode: res?.data?.mode }
  } catch (err) {
    const status = err?.response?.status
    const data = err?.response?.data
    if (status === 409 && data?.code === 'reminder_changed') {
      // Nothing was recorded, so nothing is remembered: the reminder must be
      // free to show at its new time (and at this one, were the move undone).
      // The OLD card is dropped — it no longer describes a real time — and a
      // fresh poll runs at once, so if the move left the reminder still due
      // (a hearing dragged ten minutes earlier), it is back immediately at
      // its new time instead of vanishing until the next minute's tick.  A
      // poll already on the wire is discarded (pollEpoch), not waited for:
      // it may predate the move.  (Seam audit of the 9ff73f1 follow-up fixes.)
      dismissedIds.delete(key)
      snoozedUntil.delete(key)
      pollEpoch += 1
      if (!deferPoll) await startPoll()
      return { status: 'changed', message: data.error || REMINDER_CHANGED_FALLBACK }
    }
    // Gone — deleted, or rewritten under a new id by someone saving the
    // event at the same moment (a full save re-creates its reminder rows).
    // The local hide stands for the old id.  Poll straight away rather than
    // on the next minute's tick: if the reminder was only rewritten, the
    // click did not take, and the card should come back while the user is
    // still looking at it — not reappear a minute later for no visible
    // reason.  If it was deleted, the poll changes nothing.  It must be a NEW
    // poll: fetchNow() would hand back one already on the wire, which may
    // have been sent before the save and so cannot know the new id — and
    // that older poll is discarded (pollEpoch) so it cannot land afterwards
    // and take the new card away again.  (Seam audit of the 9ff73f1
    // follow-up fixes.)
    if (status === 404) {
      pollEpoch += 1
      if (!deferPoll) await startPoll()
      return { status: 'gone' }
    }
    // Best-effort — an older API (rolling deploy) may refuse with 403, or
    // the network may blip.  The local hide stands for this session so the
    // user isn't nagged repeatedly; the next page load re-reads the server.
    return { status: 'failed' }
  }
}

// Put reminders whose PUT failed outright (a 5xx, a network blip, a 403)
// back on screen: the server recorded nothing, so the reminder is still due
// and its card must stay.  Undoes the optimistic hide and re-inserts the row
// where it sat in `shownBefore` (the list as it was when the user clicked) —
// before the first row still listed that came after it — unless a poll has
// already put that occurrence back.  The next poll re-orders from the server.
function restoreUnrecorded(entries, shownBefore) {
  const keyOf = (r) => occurrenceKey(r.reminder_id, r.reminder_due_at)
  const posBefore = new Map(shownBefore.map((r, i) => [keyOf(r), i]))
  const next = reminders.slice()
  for (const { key, row } of entries) {
    dismissedIds.delete(key)
    snoozedUntil.delete(key)
    confirmedAt.delete(key)
    if (!row || next.some(r => keyOf(r) === key)) continue
    const at = posBefore.get(key)
    let insertAt = next.length
    if (at !== undefined) {
      const j = next.findIndex(r => {
        const p = posBefore.get(keyOf(r))
        return p !== undefined && p > at
      })
      if (j !== -1) insertAt = j
    }
    next.splice(insertAt, 0, row)
  }
  reminders = next
  emit()
}

/**
 * Dismiss (or snooze, with the same opts as dismissReminder) several
 * reminders the user acted on together — the rows of ONE grouped card.
 * Only the rows passed in are touched: the caller hands over exactly what
 * the card showed, so a reminder the person never saw there is never
 * dismissed.  One PUT per row, each with its own due_at.  If any row came
 * back 409 or 404, a single fresh poll is sent once every PUT has settled
 * (the per-row branches have already discarded any older poll on the wire),
 * so a reminder that moved, or was re-created under a new id, is back on
 * screen before this resolves.
 *
 * A row whose PUT failed for any other reason (5xx, network, 403) is NOT
 * left hidden for the session, as the single-row path leaves it: the server
 * recorded nothing, so the reminder goes back on its card and the caller
 * says so (useReminders' dismissAll toasts).  Court deadlines carry legal
 * weight; a batch of up to fifteen PUTs makes a partial failure likelier
 * than one click ever did, and the card must stay for whatever failed
 * (verifier finding, 2026-10-12).
 */
async function dismissReminders(targets, opts = {}) {
  const list = Array.isArray(targets) ? targets.filter(t => t != null) : []
  if (list.length === 0) return []
  // Resolved now, while every row is still in the list: a bare id looks its
  // due time up there, and the optimistic hides below take the rows out.
  const shownBefore = reminders.slice()
  const resolved = list.map(t => {
    const { id, due } = resolveTarget(t, opts)
    const key = occurrenceKey(id, due)
    const row = (t && typeof t === 'object')
      ? t
      : shownBefore.find(r => occurrenceKey(r.reminder_id, r.reminder_due_at) === key) || null
    return { key, row }
  })
  const outcomes = await Promise.all(list.map(t => dismissReminder(t, opts, { deferPoll: true })))
  const unrecorded = resolved.filter((_, i) => outcomes[i].status === 'failed')
  if (unrecorded.length) restoreUnrecorded(unrecorded, shownBefore)
  if (outcomes.some(o => o.status === 'changed' || o.status === 'gone')) {
    pollEpoch += 1
    await startPoll()
  }
  return outcomes
}

/**
 * Desktop-app native notifications — one per reminder OCCURRENCE each time it
 * appears in the list.
 *
 *   useReminderNotifications(reminders, notify)
 *
 * `notify(row)` is called for every occurrence that was not in the list the
 * last time it changed; pass null to turn it off (not the desktop app).
 *
 * Code review of 9ff73f1, verify pass on finding #1.  The Calendar page used
 * to remember notified reminders by reminder_id for as long as it stayed
 * open.  A move keeps the same id, so a reminder the server re-armed for its
 * new time never raised a native notification — and an attorney whose window
 * is hidden to the tray sees nothing else.  Keyed by occurrence, and
 * remembered only while the occurrence stays in the list, a re-armed
 * reminder notifies at its new time, and so does one that returns when its
 * snooze or repeat interval runs out.  An occurrence that simply stays on
 * screen across polls notifies once.
 *
 * One notification per EVENT per list change (2026-10-12): the notification
 * says only "Reminder" + the event title, so five Amicus reminders for one
 * deadline coming due together used to raise five identical notifications.
 * Now the first new occurrence of an event in a given list notifies and the
 * rest of that event's new occurrences are marked shown silently.  A later
 * list that brings a NEW occurrence for the same event (the "1 hour before"
 * after the "1 day before") still notifies — only same-batch duplicates are
 * folded.  Rows with no event id (an API that predates e.*) never fold.
 */
export function useReminderNotifications(reminders, notify) {
  const shownRef = useRef(new Set())
  useEffect(() => {
    if (typeof notify !== 'function') return
    const current = new Set()
    const notifiedEvents = new Set()
    for (const r of reminders || []) {
      const key = occurrenceKey(r.reminder_id, r.reminder_due_at)
      current.add(key)
      if (shownRef.current.has(key)) continue
      const eventKey = r.id != null ? String(r.id) : null
      if (eventKey !== null && notifiedEvents.has(eventKey)) continue
      if (eventKey !== null) notifiedEvents.add(eventKey)
      notify(r)
    }
    shownRef.current = current
  }, [reminders, notify])
}

export default function useReminders() {
  const [localReminders, setLocalReminders] = useState(reminders)
  const toast = useToast()

  useEffect(() => {
    subscribers.add(setLocalReminders)
    // First subscriber kicks off the poll; sync current snapshot for late joiners.
    if (subscribers.size === 1) {
      startPolling()
    } else {
      setLocalReminders(reminders)
    }
    return () => {
      subscribers.delete(setLocalReminders)
      if (subscribers.size === 0) stopPolling()
    }
  }, [])

  // The toast lives here, not in each caller, so every surface that lets the
  // user dismiss a reminder explains a 409 the same way.  Non-blocking: the
  // card is already gone, this only says why and when it will be back.
  const dismiss = useCallback(async (target, opts) => {
    const outcome = await dismissReminder(target, opts)
    if (outcome.status === 'changed') toast.info(outcome.message)
    return outcome
  }, [toast])

  // Same toast, said once: when an event moves, every reminder on its card
  // answers 409 with the same message, and five identical toasts explain
  // nothing the first did not.  Reminders whose PUT failed outright are back
  // on the card (dismissReminders); one toast says so.
  const dismissAll = useCallback(async (targets, opts) => {
    const outcomes = await dismissReminders(targets, opts)
    const messages = new Set(outcomes.filter(o => o.status === 'changed').map(o => o.message))
    for (const m of messages) toast.info(m)
    const failed = outcomes.filter(o => o.status === 'failed').length
    if (failed > 0) {
      const isSnooze = !!opts && (opts.minutes != null || !!opts.snooze_until)
      const verb = isSnooze ? 'snoozed' : 'dismissed'
      toast.error(failed === 1
        ? `1 reminder could not be ${verb}, so it is still showing. Try again.`
        : `${failed} reminders could not be ${verb}, so they are still showing. Try again.`)
    }
    return outcomes
  }, [toast])

  return {
    reminders: localReminders,
    refresh: fetchNow,
    dismiss,
    dismissAll,
  }
}
